import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { ConversationItem, MatchItem } from "../types";

type ConversationPage = { conversations: ConversationItem[]; nextCursor: string | null };
export function Chats({ onOpen, onMatches }: { onOpen(conversation: ConversationItem): void; onMatches(): void }) {
  const [items, setItems] = useState<ConversationItem[] | null>(null);
  const [matches, setMatches] = useState<MatchItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    const [conversations, pairs] = await Promise.allSettled([
      api<ConversationPage>("/v1/conversations?limit=30"),
      api<{ matches: MatchItem[] }>("/v1/matches"),
    ]);
    if (conversations.status === "fulfilled") {
      setItems(conversations.value.conversations);
      setCursor(conversations.value.nextCursor);
      setError("");
    } else setError("Не удалось обновить чаты. Попробуйте ещё раз.");
    if (pairs.status === "fulfilled") setMatches(pairs.value.matches);
    else if (conversations.status === "fulfilled") setError("Не удалось обновить мэтчи. Чаты остаются доступны.");
  }, []);
  useEffect(() => {
    void load();
    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };
    const timer = window.setInterval(refresh, 30000);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);
  const loadMore = async () => {
    if (!cursor || more) return;
    setMore(true);
    try {
      const r = await api<ConversationPage>(`/v1/conversations?limit=30&before=${encodeURIComponent(cursor)}`);
      setItems((v) => [...(v ?? []), ...r.conversations.filter((item) => !v?.some((old) => old.id === item.id))]);
      setCursor(r.nextCursor);
    } catch {
      setError("Не удалось загрузить больше чатов.");
    } finally {
      setMore(false);
    }
  };
  return (
    <section className="content-page chats-page">
      <div className="reference-matches-heading">
        <h2>Мэтчи</h2>
        <button type="button" onClick={onMatches}>
          Все ({matches.length}) ›
        </button>
      </div>
      {matches.length > 0 && (
        <div className="reference-matches-strip">
          {matches.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() =>
                onOpen({
                  id: m.conversationId,
                  matchId: m.id,
                  user: m.user,
                  createdAt: m.createdAt,
                  unreadCount: 0,
                  lastMessage: null,
                })
              }
            >
              {m.user.photoUrl ? <img src={m.user.photoUrl} alt="" /> : <span className="avatar-fallback" />}
              {m.user.displayName}
            </button>
          ))}
        </div>
      )}
      <div className="reference-matches-heading">
        <h2>Чаты</h2>
      </div>
      {error && (
        <div role="alert" className="reference-error">
          {error}
          <button type="button" className="secondary-button" onClick={() => void load()}>
            Повторить
          </button>
        </div>
      )}
      {!items && !error ? (
        <div className="list-skeleton" role="status" aria-label="Загрузка чатов" />
      ) : items?.length === 0 ? (
        <div className="empty-state inline-empty">
          <h2>Чатов пока нет</h2>
          <p>После взаимного лайка можно начать общение.</p>
        </div>
      ) : (
        <div className="conversation-list">
          {items?.map((c) => (
            <button type="button" className="conversation-row" key={c.id} onClick={() => onOpen(c)}>
              {c.user.photoUrl ? <img src={c.user.photoUrl} alt="" /> : <span className="avatar-fallback" />}
              <span className="conversation-copy">
                <span className="conversation-title">
                  <strong>{c.user.displayName}</strong>
                  {c.unreadCount > 0 && (
                    <span className="unread-badge" role="status" aria-label={`Непрочитанных: ${c.unreadCount}`}>
                      {c.unreadCount > 99 ? "99+" : c.unreadCount}
                    </span>
                  )}
                </span>
                <span className={c.unreadCount ? "conversation-preview unread" : "conversation-preview"}>
                  {c.lastMessage
                    ? `${c.lastMessage.mine ? "Вы: " : ""}${c.lastMessage.body ?? ""}`
                    : "Можно начинать разговор"}
                </span>
              </span>
              <span className="conversation-time">
                {c.lastMessage?.createdAt
                  ? new Date(c.lastMessage.createdAt).toLocaleDateString("ru-RU", { day: "2-digit", month: "short" })
                  : ""}
              </span>
            </button>
          ))}
        </div>
      )}
      {cursor && (
        <button type="button" className="load-more-conversations" disabled={more} onClick={() => void loadMore()}>
          {more ? "Загружаем…" : "Показать ещё"}
        </button>
      )}
    </section>
  );
}
